import {
  BillingOperationResult,
  useAsyncOperationWithResult,
  useSiteNextBillingRun,
  useSiteOperationalActions,
} from "@inrange/building-manager-api-client";
import { Site } from "@inrange/building-manager-api-client/models-site";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import InvoicePreviewModal from "../billing/InvoicePreviewModal";
import DbRecordsWritten from "./DbRecordsWritten";
import {
  computeMonthsToProcess,
  formatDateYMD,
  operationStatusMessage,
} from "./utils";

const SiteReadingsInvoice = ({
  site,
  isFixedLease,
}: {
  site: Site;
  isFixedLease: boolean;
}) => {
  const { processBilling } = useSiteOperationalActions();
  const { data: nextBillingRun } = useSiteNextBillingRun(site.id);

  const monthsToProcess = useMemo(
    () =>
      computeMonthsToProcess(
        site.commerciallyOperationalDate ?? undefined,
        site.timeZone
      ),
    [site.commerciallyOperationalDate, site.timeZone]
  );

  const [selectedMonth, setSelectedMonth] = useState<string | undefined>();

  useEffect(() => {
    if (monthsToProcess.length === 0) {
      setSelectedMonth(undefined);
      return;
    }
    if (!selectedMonth || !monthsToProcess.includes(selectedMonth)) {
      setSelectedMonth(monthsToProcess[monthsToProcess.length - 1]);
    }
  }, [monthsToProcess, selectedMonth]);
  const [loadingAction, setLoadingAction] = useState<
    "generate-readings" | "preview-current" | null
  >(null);
  const [invoiceDate, setInvoiceDate] = useState<Date>(new Date());
  const [invoicePreviewMonth, setInvoicePreviewMonth] = useState<string | null>(
    null
  );

  const onSettled = useCallback(() => {
    setLoadingAction(null);
  }, []);

  const { asyncOperation, setOperationKey, result, setResult } =
    useAsyncOperationWithResult<BillingOperationResult>({
      siteId: site.id,
      onSettled,
    });

  const isProcessing = !!loadingAction;

  const runProcessBilling = async (
    action: "current_month" | `month:${string}`,
    buttonAction: "generate-readings" | "preview-current",
    opts: {
      readingDbWrite: boolean;
      replace: boolean;
      attachReadings: boolean;
    }
  ) => {
    setResult(null);
    setLoadingAction(buttonAction);
    try {
      const response = await processBilling.mutateAsync({
        siteId: site.id,
        readingAction: action,
        readingDbInsert: opts.readingDbWrite,
        readingDbUpdate: opts.replace,
        attachReadings: opts.attachReadings,
        invoiceDate: formatDateYMD(invoiceDate),
      });
      setOperationKey(response.operationKey);
    } catch (error) {
      const axiosData = (
        error as { response?: { data?: { message?: string } } }
      )?.response?.data;
      const errorMessage =
        axiosData?.message ||
        (error instanceof Error ? error.message : "An unknown error occurred");
      setResult({ type: "error", message: errorMessage });
      setLoadingAction(null);
    }
  };

  // Pretty-print the endpoint's "YYYY-MM-DD" without a UTC shift (build a
  // local Date from the parts rather than parsing the string as UTC midnight).
  const formatNextRun = (iso: string): string => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      {nextBillingRun?.nextRunDate && (
        <div className="text-muted small mb-2">
          Next automatic run: {formatNextRun(nextBillingRun.nextRunDate)}
        </div>
      )}
      <Row className="g-2 align-items-end">
        <Col style={{ width: "220px", flex: "0 0 auto" }}>
          <Form.Group controlId="readingsMonthToProcess">
            <Form.Label>Month to process</Form.Label>
            <Form.Select
              value={selectedMonth ?? ""}
              onChange={(event) =>
                setSelectedMonth(event.target.value || undefined)
              }
              disabled={monthsToProcess.length === 0}
            >
              {monthsToProcess.length === 0 ? (
                <option value="">No complete months available</option>
              ) : (
                monthsToProcess
                  .slice()
                  .reverse()
                  .map((month, index) => (
                    <option key={month} value={month}>
                      {month}
                      {index === 0 && " (latest)"}
                    </option>
                  ))
              )}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col style={{ width: "220px", flex: "0 0 auto" }}>
          <Form.Group controlId="invoiceDate">
            <Form.Label>Invoice date</Form.Label>
            <div>
              <DatePicker
                selected={invoiceDate}
                onChange={(date: Date | null) => {
                  if (date) setInvoiceDate(date);
                }}
                dateFormat="dd MMM yyyy"
                calendarStartDay={1}
                disabled={isProcessing}
                className="form-control"
              />
            </div>
          </Form.Group>
        </Col>
        <Col sm="auto" className="d-flex align-items-center gap-2">
          <Button
            variant="primary"
            onClick={() => {
              if (!selectedMonth) return;
              const isLatestMonth =
                selectedMonth === monthsToProcess[monthsToProcess.length - 1];
              runProcessBilling(`month:${selectedMonth}`, "generate-readings", {
                readingDbWrite: true,
                replace: isLatestMonth,
                attachReadings: true,
              });
            }}
            disabled={!selectedMonth || isProcessing}
          >
            {isProcessing && loadingAction === "generate-readings" ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {operationStatusMessage(asyncOperation.data?.status, {
                  progress: asyncOperation.data?.progress,
                  generateInvoice: !isFixedLease,
                })}
              </>
            ) : isFixedLease ? (
              "Generate readings"
            ) : (
              "Generate readings and invoice"
            )}
          </Button>
        </Col>
        {!isFixedLease && (
          <Col sm="auto">
            <Button
              variant="outline-primary"
              onClick={() => {
                if (!selectedMonth) return;
                setInvoicePreviewMonth(selectedMonth);
              }}
              disabled={!selectedMonth || isProcessing}
            >
              Generate invoice w/ adjustments
            </Button>
          </Col>
        )}
      </Row>

      <div style={{ fontWeight: "500" }}>Debug actions for current month</div>
      <Row className="g-2 align-items-end">
        <Col sm="auto" className="d-flex align-items-center gap-2">
          <Button
            variant="secondary"
            onClick={() =>
              runProcessBilling("current_month", "preview-current", {
                readingDbWrite: false,
                replace: false,
                attachReadings: true,
              })
            }
            disabled={isProcessing}
          >
            {isProcessing && loadingAction === "preview-current" ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {operationStatusMessage(asyncOperation.data?.status, {
                  progress: asyncOperation.data?.progress,
                  generateInvoice: !isFixedLease,
                })}
              </>
            ) : (
              "Generate preview readings file"
            )}
          </Button>
        </Col>
      </Row>

      {result && (
        <Alert
          variant={result.type === "success" ? "success" : "danger"}
          dismissible
          onClose={() => setResult(null)}
        >
          {result.type === "success" && result.response ? (
            <>
              <div>Readings and invoice generated successfully.</div>
              {result.response.files_attached.length > 0 && (
                <div className="mt-2">
                  <div className="fw-bold">Files attached:</div>
                  <ul>
                    {result.response.files_attached.map((f, index) => (
                      <li key={index}>{f.fileName}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.response.reading_db_update_stats &&
                Object.keys(result.response.reading_db_update_stats).length >
                  0 && (
                  <DbRecordsWritten
                    dbRecordsWritten={result.response.reading_db_update_stats}
                    esys={site.esys}
                  />
                )}
            </>
          ) : (
            result.message
          )}
        </Alert>
      )}

      {invoicePreviewMonth && (
        <InvoicePreviewModal
          siteId={site.id}
          month={invoicePreviewMonth}
          currencyCode={site.currencyCode}
          initialInvoiceDate={invoiceDate}
          onClose={() => setInvoicePreviewMonth(null)}
        />
      )}
    </>
  );
};

export default SiteReadingsInvoice;
